/**
 * Gravity Forms Date Field - Inline Datepicker and Date Restrictions
 *
 * For fields in step 1 (with .ngrmsf-step-1 class): converts to inline datepickers (always visible).
 * For all date fields: limits selectable dates to today and future dates only.
 * For appointment fields (with .appointment-desired-date class): also disables Sundays.
 *
 * @package Novigran_Multistep_Form
 * @since   1.5.6
 * @author  Adrián Ortiz Arandes
 */

/**
 * Initialize datepicker modifications for all Gravity Forms date fields
 * 
 * Step 1 fields: Converts standard popup datepickers to inline display.
 * All fields: Applies minDate to disable past dates.
 * Appointment fields: Disables Sundays via beforeShowDay callback.
 */
export function initDatepickerFilter() {
    var $ = jQuery;

    // Wait for Gravity Forms to initialize datepickers, then configure them
    $(document).on('gform_post_render', function(event, formId) {
        configureDatepickerFields(formId);
    });

    // Also run on page load for forms already rendered
    $(window).on('load', function() {
        // Find all Gravity Forms on the page
        $('.gform_wrapper').each(function() {
            var formId = $(this).attr('id');
            if (formId) {
                // Extract numeric form ID from "gform_wrapper_X"
                var match = formId.match(/gform_wrapper_(\d+)/);
                if (match) {
                    configureDatepickerFields(match[1]);
                }
            }
        });
    });
}

/**
 * Configure datepicker fields inside .gfield.inline-datepicker containers
 * 
 * - Converts step 1 fields to inline datepickers (always visible)
 * - Applies future date restrictions to all datepickers (minDate: 0)
 * - Disables Sundays for appointment fields (with .appointment-desired-date class)
 * 
 * @param {number|string} formId The Gravity Forms form ID
 */
function configureDatepickerFields(formId) {
    var $ = jQuery;

    // Find all date field inputs inside .gfield.inline-datepicker containers
    var $datepickerInputs = $('#gform_wrapper_' + formId + ' .gfield input.datepicker');
    
    // Only proceed if datepicker inputs exist inside .gfield.inline-datepicker
    if ($datepickerInputs.length === 0) {
        return;
    }

    $datepickerInputs.each(function() {
        var $input = $(this);
        
        // Check if field is in step 1
        var $isStep1 = $input.closest('.ngrmsf-step-1').length > 0;
        
        // Get the field container
        var $fieldContainer = $input.closest('.gfield');
        
        // Check if this is an appointment field (for Sunday disabling)
        var hasAppointmentClass = $fieldContainer.hasClass('appointment-desired-date');

        // Handle step 1 fields: convert to inline datepicker
        if ($isStep1) {
            // Skip if already converted
            if ($input.data('inline-converted')) {
                return;
            }

            // Mark as converted to prevent duplicate processing
            $input.data('inline-converted', true);

            var $inputWrapper = $input.closest('.ginput_container');

            // Destroy the existing popup datepicker
            if ($input.hasClass('hasDatepicker')) {
                $input.datepicker('destroy');
            }

            // Create inline container
            var $inlineContainer = $('<div class="gf-datepicker-inline"></div>');
            $inputWrapper.append($inlineContainer);

            // Hide the original input (keep it for form submission)
            $input.addClass('gf-datepicker-hidden-input');

            // Build datepicker options (dd/mm/yy = day/month/4-digit-year in jQuery UI)
            var dateFormat = $input.data('dateFormat') || 'dd/mm/yy';
            var datepickerOptions = {
                altField: $input,
                altFormat: dateFormat,
                dateFormat: dateFormat,
                minDate: 0, // Today and future dates only
                showOtherMonths: false, // Don't show dates from other months
                changeMonth: true,
                changeYear: true,
                monthNamesShort: [
                    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                ],
                onSelect: function(dateText) {
                    // Trigger change event on the hidden input for validation
                    $input.val(dateText).trigger('change');
                }
            };
            
            // Disable Sundays for appointment desired date fields
            if (hasAppointmentClass) {
                datepickerOptions.beforeShowDay = function(date) {
                    var day = date.getDay();
                    return [day !== 0, ''];
                };
            }

            // Initialize inline datepicker on the container
            $inlineContainer.datepicker(datepickerOptions);

            // Add gravity-theme class to the child ui-datepicker element created by jQuery UI
            $inlineContainer.find('.ui-datepicker').addClass('gravity-theme');

            // If input already has a value, set it in the datepicker
            if ($input.val()) {
                $inlineContainer.datepicker('setDate', $input.val());
            }
        } else {
            // For non-step-1 fields: modify existing datepicker options
            // Use a function to apply options, retrying if datepicker not yet initialized
            var applyDatepickerOptions = function() {

                if ($input.hasClass('hasDatepicker')) {

                    // Update options: limit to today and future dates only
                    var updatedOptions = {
                        minDate: 0,
                        showOtherMonths: false // Don't show dates from other months (same as inline datepicker)
                    };
                    
                    // Disable Sundays for appointment desired date fields
                    if (hasAppointmentClass) {
                        // Get existing beforeShowDay if it exists
                        var existingBeforeShowDay = $input.datepicker('option', 'beforeShowDay');
                        
                        updatedOptions.beforeShowDay = function(date) {
                            var day = date.getDay();
                            var isSelectable = day !== 0; // Disable Sundays
                            
                            // If there's an existing beforeShowDay, call it and combine results
                            if (existingBeforeShowDay && typeof existingBeforeShowDay === 'function') {
                                var existingResult = existingBeforeShowDay(date);
                                // Combine: date must pass both checks
                                isSelectable = isSelectable && existingResult[0];
                            }
                            
                            // Return [selectable, className]
                            return [isSelectable, ''];
                        };
                    }
                    
                    // Apply updated options
                    $input.datepicker('option', updatedOptions);
                } else {
                    // Retry after a short delay if datepicker not yet initialized
                    setTimeout(applyDatepickerOptions, 50);
                }
            };
            
            // Start applying options
            applyDatepickerOptions();
        }
    });
}
